/*************************************************************
 * WARNING: This file was automatically generated by script. *
 *************************************************************/

/* eslint-disable @typescript-eslint/no-unused-vars */

import type { CancelTokenSource } from 'axios';
import type { CallApiResult } from '@tmapy/beetlejuice-utils';
import type { QueryArgs } from '../../utils/queryArgs';
import { callApi } from '../../utils/api';
import { queryArgs } from '../../utils/queryArgs';
import type { PaginatedEpsiOrderListResponse } from '../types/PaginatedEpsiOrderList';

import type { UseResource } from '../../hooks/useResource';
import { useResource } from '../../hooks/useResource';

export type EpsiOrdersListRequest = {} | null | undefined;
export type EpsiOrdersListResponse = PaginatedEpsiOrderListResponse;

export type EpsiOrdersListQuery = {
  /**
   * ```yaml
   * name: page
   * required: false
   * in: query
   * description: A page number within the paginated result set.
   * schema:
   *   type: integer
   * ```
   */
  page?: number;

  /**
   * ```yaml
   * name: page_size
   * required: false
   * in: query
   * description: Number of results to return per page.
   * schema:
   *   type: integer
   * ```
   */
  page_size?: number;

  /**
   * ```yaml
   * in: query
   * name: state
   * schema:
   *   type: string
   *   enum:
   *     - APPROVED
   *     - DMVS_COMMUNICATION_FAILED
   *     - EXPIRED
   *     - FAILED
   *     - INTERRUPTED
   *     - PROCESSING
   *     - PUBLISHED
   *     - REJECTED
   *     - WAITING_FOR_APPROVAL
   *     - WAITING_FOR_INTERRUPTION
   *     - WAITING_FOR_PROCESSING
   * description: |-
   *   * `WAITING_FOR_APPROVAL` - Waiting for approval
   *   * `WAITING_FOR_PROCESSING` - Waiting for processing
   *   * `PROCESSING` - Processing
   *   * `WAITING_FOR_INTERRUPTION` - Waiting for interruption
   *   * `INTERRUPTED` - Interrupt
   *   * `REJECTED` - Rejected
   *   * `PUBLISHED` - Published
   *   * `FAILED` - Failed
   *   * `DMVS_COMMUNICATION_FAILED` - DMVS communication failed
   *   * `EXPIRED` - Expired
   *   * `APPROVED` - Approved
   * ```
   */
  state?:
    | 'APPROVED'
    | 'DMVS_COMMUNICATION_FAILED'
    | 'EXPIRED'
    | 'FAILED'
    | 'INTERRUPTED'
    | 'PROCESSING'
    | 'PUBLISHED'
    | 'REJECTED'
    | 'WAITING_FOR_APPROVAL'
    | 'WAITING_FOR_INTERRUPTION'
    | 'WAITING_FOR_PROCESSING';

  /**
   * ```yaml
   * in: query
   * name: state__in
   * schema:
   *   type: array
   *   items:
   *     type: string
   *     enum:
   *       - APPROVED
   *       - DMVS_COMMUNICATION_FAILED
   *       - EXPIRED
   *       - FAILED
   *       - INTERRUPTED
   *       - PROCESSING
   *       - PUBLISHED
   *       - REJECTED
   *       - WAITING_FOR_APPROVAL
   *       - WAITING_FOR_INTERRUPTION
   *       - WAITING_FOR_PROCESSING
   * description: |-
   *   * `WAITING_FOR_APPROVAL` - Waiting for approval
   *   * `WAITING_FOR_PROCESSING` - Waiting for processing
   *   * `PROCESSING` - Processing
   *   * `WAITING_FOR_INTERRUPTION` - Waiting for interruption
   *   * `INTERRUPTED` - Interrupt
   *   * `REJECTED` - Rejected
   *   * `PUBLISHED` - Published
   *   * `FAILED` - Failed
   *   * `DMVS_COMMUNICATION_FAILED` - DMVS communication failed
   *   * `EXPIRED` - Expired
   *   * `APPROVED` - Approved
   * explode: true
   * style: form
   * ```
   */
  state__in?: (
    | 'APPROVED'
    | 'DMVS_COMMUNICATION_FAILED'
    | 'EXPIRED'
    | 'FAILED'
    | 'INTERRUPTED'
    | 'PROCESSING'
    | 'PUBLISHED'
    | 'REJECTED'
    | 'WAITING_FOR_APPROVAL'
    | 'WAITING_FOR_INTERRUPTION'
    | 'WAITING_FOR_PROCESSING'
  )[];
};

/**
 * GET /dmx-server/epsi_orders/
 *
 * ```yaml
 * operationId: epsi_orders_list
 * tags:
 *   - epsi_orders
 * security:
 *   - jwtAuth: []
 *   - cookieAuth: []
 * responses:
 *   '200':
 *     content:
 *       application/json:
 *         schema:
 *           $ref: '#/components/schemas/PaginatedEpsiOrderList'
 *     description: ''
 * ```
 */
export const epsiOrdersListOperation = (rest?: EpsiOrdersListQuery | null) =>
  ({
    method: 'GET',
    path: `/dmx-server/epsi_orders/${queryArgs(rest as QueryArgs)}`,
  }) as {
    method: 'GET';
    path: string & { _templateUrl: '/dmx-server/epsi_orders/' };
  };

/**
 * GET /dmx-server/epsi_orders/
 *
 * ```yaml
 * operationId: epsi_orders_list
 * tags:
 *   - epsi_orders
 * security:
 *   - jwtAuth: []
 *   - cookieAuth: []
 * responses:
 *   '200':
 *     content:
 *       application/json:
 *         schema:
 *           $ref: '#/components/schemas/PaginatedEpsiOrderList'
 *     description: ''
 * ```
 */
export const epsiOrdersList = async ({
  query,
  request,
  onUploadProgress,
  cancelToken,
}: {
  query?: EpsiOrdersListQuery;
  request?: {} | null;
  onUploadProgress?: null | ((progressEvent: any) => void);
  cancelToken?: null | CancelTokenSource;
}): Promise<CallApiResult<PaginatedEpsiOrderListResponse>> => {
  const operation = epsiOrdersListOperation(query);
  return callApi(
    `${globalThis.DMX_CONFIG.server}${operation.path}`,
    'GET',
    request,
    onUploadProgress,
    cancelToken,
  );
};

/**
 * GET /dmx-server/epsi_orders/
 */
export const useEpsiOrdersListResource = (
  query: EpsiOrdersListQuery | null,
  useCached?: boolean,
): UseResource<EpsiOrdersListResponse> => {
  return useResource<EpsiOrdersListResponse>({
    path: query === null ? null : epsiOrdersListOperation(query).path,
    apiUrl: globalThis.DMX_CONFIG.server,
    useCached,
  });
};
